/**
 * Typed wrappers for the Leave Management module + the cross-module
 * notification bell. Paths and shapes are taken VERBATIM from
 * backend/internal/router/router.go and the leave.go / notifications.go
 * handlers.
 *
 * Cookie-only auth + the CSRF interceptor + 401→refresh are handled by
 * the shared axios instance (lib/api/client.ts). The Leave routes are
 * fenced server-side by RequireFeature("leave_management"); the api
 * client turns the backend's 403 { error: "upgrade_required" } into a
 * /pricing redirect, and other 403s reject normally (the caller renders
 * a graceful state). The /notifications routes are auth-only (NOT
 * feature-gated) — the bell is cross-module by design.
 *
 * No client-supplied tenant_id / user_id is ever sent: the server
 * derives both from the session for every route.
 */

import { api } from "./client";
import type {
  CreateLeaveRequestRequest,
  CreateLeaveTypeRequest,
  LeaveBalanceResponse,
  LeaveDecisionRequest,
  LeaveRequest,
  LeaveRequestDetail,
  LeaveSettings,
  LeaveType,
  ListLeaveRequestsResponse,
  ListLeaveTypesResponse,
  ListNotificationsResponse,
  Notification,
  UnreadCountResponse,
  UpdateLeaveSettingsRequest,
  UpdateLeaveTypeRequest,
} from "@/types/leave";
import type {
  CompOffBalanceResponse,
  CompOffCredit,
  GrantCompOffRequest,
  ListCompOffResponse,
} from "@/types/comp-off";

export const LEAVE_BASE = "/api/v1/leave";
export const NOTIF_BASE = "/api/v1/notifications";

// ---------------------------------------------------------------------------
// Leave types (authenticated, RequireFeature("leave_management"))
// ---------------------------------------------------------------------------

// GET /leave/types — non-HR sees active only; HR sees all (server-side).
export async function listLeaveTypes(
  signal?: AbortSignal,
): Promise<ListLeaveTypesResponse> {
  const { data } = await api.get<ListLeaveTypesResponse>(
    `${LEAVE_BASE}/types`,
    { signal },
  );
  return data;
}

// POST /leave/types — RBAC (company_admin/hr_manager) + CSRF + Audit.
export async function createLeaveType(
  body: CreateLeaveTypeRequest,
): Promise<LeaveType> {
  const { data } = await api.post<LeaveType>(`${LEAVE_BASE}/types`, body);
  return data;
}

// PATCH /leave/types/{id} — RBAC + CSRF + Audit. A present-and-null
// default_balance_days makes the type unlimited; absent = unchanged.
export async function updateLeaveType(
  id: number,
  body: UpdateLeaveTypeRequest,
): Promise<LeaveType> {
  const { data } = await api.patch<LeaveType>(
    `${LEAVE_BASE}/types/${id}`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Leave requests (role-FILTERED in SQL — the boundary is server-side)
// ---------------------------------------------------------------------------

// GET /leave/requests[?status=][&scope=self][&exclude_self=1][&mine=1] —
// employee=own, manager=own+reports, HR=tenant-all. `scope=self` NARROWS
// the result to the caller's own rows regardless of role (server-side
// override added with the calendar slice). `exclude_self=1` narrows an
// approver's list to rows they can ACT on (own submissions removed);
// `mine=1` narrows to only the caller's own rows (the Me-surface list).
// Status filter is independent.
export async function listLeaveRequests(
  params?: {
    status?: string;
    scope?: "self";
    exclude_self?: boolean;
    mine?: boolean;
    /**
     * Narrowing-only per-employee filter (employee-detail Leave tab).
     * The server applies it AFTER the role-derived scope, so it can only
     * shrink an already-authorized slice — a plain employee passing
     * someone else's id just gets an empty list.
     */
    user_id?: number;
    /** Opt-in paging (1..200); the response then carries next_offset. */
    limit?: number;
    offset?: number;
  },
  signal?: AbortSignal,
): Promise<ListLeaveRequestsResponse> {
  const { exclude_self, mine, ...rest } = params ?? {};
  const { data } = await api.get<ListLeaveRequestsResponse>(
    `${LEAVE_BASE}/requests`,
    {
      params: {
        ...rest,
        ...(exclude_self ? { exclude_self: 1 } : {}),
        ...(mine ? { mine: 1 } : {}),
      },
      signal,
    },
  );
  return data;
}

// POST /leave/requests — own request; server derives user/tenant and
// computes total_days (weekends + tenant holidays excluded). CSRF+Audit.
export async function createLeaveRequest(
  body: CreateLeaveRequestRequest,
): Promise<LeaveRequest> {
  const { data } = await api.post<LeaveRequest>(
    `${LEAVE_BASE}/requests`,
    body,
  );
  return data;
}

// GET /leave/requests/{id} (wave 2) — one request with what the caller may
// do with it. Owner / HR / approver chain / live delegate (pending) /
// decider; 404 for anyone else.
export async function getLeaveRequest(
  id: number,
  signal?: AbortSignal,
): Promise<LeaveRequestDetail> {
  const { data } = await api.get<LeaveRequestDetail>(
    `${LEAVE_BASE}/requests/${id}`,
    { signal },
  );
  return data;
}

// POST /leave/requests/{id}/approve — approver chain, OOO delegate or HR;
// `self_approve: true` is the solo-admin path. Returns the updated
// request. CSRF+Audit.
export async function approveLeaveRequest(
  id: number,
  body?: LeaveDecisionRequest,
): Promise<LeaveRequest> {
  const { data } = await api.post<LeaveRequest>(
    `${LEAVE_BASE}/requests/${id}/approve`,
    body ?? {},
  );
  return data;
}

// POST /leave/requests/{id}/reject — manager-of-that-user or HR.
export async function rejectLeaveRequest(
  id: number,
  body?: LeaveDecisionRequest,
): Promise<LeaveRequest> {
  const { data } = await api.post<LeaveRequest>(
    `${LEAVE_BASE}/requests/${id}/reject`,
    body ?? {},
  );
  return data;
}

// POST /leave/requests/{id}/cancel — No body. Two server paths:
//  - requester: their OWN pending request, or approved leave that hasn't
//    started (409 leave_started once it has);
//  - hr_manager / company_admin: someone else's APPROVED leave, on their
//    behalf (409 payroll_locked if a month it touches has a locked run).
// Anyone else → 404.
export async function cancelLeaveRequest(
  id: number,
): Promise<LeaveRequest> {
  const { data } = await api.post<LeaveRequest>(
    `${LEAVE_BASE}/requests/${id}/cancel`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Leave balances
// ---------------------------------------------------------------------------

// GET /leave/balance[?user_id=&year=&date=] — default self. user_id is
// honored only for HR (anyone) / a manager (own reports); the server
// authorizes. Omit `year` to let the server resolve the leave-year (the one
// containing `date`, else today) under the tenant's anchor.
export async function getLeaveBalance(
  params?: { user_id?: number; year?: number; date?: string },
  signal?: AbortSignal,
): Promise<LeaveBalanceResponse> {
  const { data } = await api.get<LeaveBalanceResponse>(
    `${LEAVE_BASE}/balance`,
    { params, signal },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Leave settings (063: leave-year anchor + comp-off / probation config)
// ---------------------------------------------------------------------------

// GET /leave/settings — any tenant user reads (anchor affects everyone's view).
export async function getLeaveSettings(
  signal?: AbortSignal,
): Promise<LeaveSettings> {
  const { data } = await api.get<LeaveSettings>(`${LEAVE_BASE}/settings`, {
    signal,
  });
  return data;
}

// PATCH /leave/settings — HR-admin only (server-gated). CSRF + Audit.
export async function updateLeaveSettings(
  body: UpdateLeaveSettingsRequest,
): Promise<LeaveSettings> {
  const { data } = await api.patch<LeaveSettings>(
    `${LEAVE_BASE}/settings`,
    body,
  );
  return data;
}

// ---------------------------------------------------------------------------
// Comp-off (063) — under /leave/comp-off/*. Consumption flows through the
// normal leave-request flow against the is_comp_off type (not here).
// ---------------------------------------------------------------------------

// GET /leave/comp-off[?user_id=&status=] — self; ?user_id= for mgr-of / HR.
export async function listCompOff(
  params?: { user_id?: number; status?: string },
  signal?: AbortSignal,
): Promise<ListCompOffResponse> {
  const { data } = await api.get<ListCompOffResponse>(
    `${LEAVE_BASE}/comp-off`,
    { params, signal },
  );
  return data;
}

// GET /leave/comp-off/balance[?user_id=] — SUM of available credit-days.
export async function getCompOffBalance(
  params?: { user_id?: number },
  signal?: AbortSignal,
): Promise<CompOffBalanceResponse> {
  const { data } = await api.get<CompOffBalanceResponse>(
    `${LEAVE_BASE}/comp-off/balance`,
    { params, signal },
  );
  return data;
}

// POST /leave/comp-off/grant — manager-of target OR HR-admin. CSRF + Audit.
export async function grantCompOff(
  body: GrantCompOffRequest,
): Promise<CompOffCredit> {
  const { data } = await api.post<CompOffCredit>(
    `${LEAVE_BASE}/comp-off/grant`,
    body,
  );
  return data;
}

// POST /leave/comp-off/{id}/revoke — HR-admin; unconsumed available only.
export async function revokeCompOff(id: number): Promise<CompOffCredit> {
  const { data } = await api.post<CompOffCredit>(
    `${LEAVE_BASE}/comp-off/${id}/revoke`,
    {},
  );
  return data;
}

// ---------------------------------------------------------------------------
// Notifications — the cross-module bell (auth-only, NOT feature-gated)
// ---------------------------------------------------------------------------

// GET /notifications[?limit=] — newest first; limit clamped [1,100],
// default 30. Single-shot list (no offset/page param exists).
export async function listNotifications(
  params?: { limit?: number },
  signal?: AbortSignal,
): Promise<ListNotificationsResponse> {
  const { data } = await api.get<ListNotificationsResponse>(NOTIF_BASE, {
    params,
    signal,
  });
  return data;
}

// GET /notifications/unread-count → { unread }
export async function getUnreadCount(
  signal?: AbortSignal,
): Promise<UnreadCountResponse> {
  const { data } = await api.get<UnreadCountResponse>(
    `${NOTIF_BASE}/unread-count`,
    { signal },
  );
  return data;
}

// POST /notifications/{id}/read — idempotent (a foreign id is a no-op
// server-side). CSRF+Audit.
export async function markNotificationRead(
  id: number,
): Promise<void> {
  await api.post(`${NOTIF_BASE}/${id}/read`, {});
}

// POST /notifications/read-all — clears the caller's whole feed. CSRF+Audit.
export async function markAllNotificationsRead(): Promise<void> {
  await api.post(`${NOTIF_BASE}/read-all`, {});
}

export type { Notification };
